import { useEffect, useMemo, useState } from "react";
import { JobOrder } from "../../interfaces/job.interface";
import PdpService, { PdpDriver, errorMessage } from "../../services/PdpService";
import { usePermissions } from "../../hooks/usePermissions";

export default function PdpTripAssignment({ job, onAssigned }: {job:JobOrder;onAssigned:()=>Promise<void>}) {
  const {canEditJob}=usePermissions();
  const canAssign=canEditJob();
  const trips=useMemo(()=>job.jobBreakups.flatMap(b=>b.jobBreakUpDetails).filter(t=>!t.driverIdName && ["INITIATED","STARTED"].includes(t.tripStatus)),[job]);
  const [tripId,setTripId]=useState("");const [driverId,setDriverId]=useState("");
  const [drivers,setDrivers]=useState<PdpDriver[]>([]);const [skip,setSkip]=useState(0);const [total,setTotal]=useState(0);
  const [reason,setReason]=useState("");const [ack,setAck]=useState(false);
  const [error,setError]=useState("");const [busy,setBusy]=useState(false);const [loading,setLoading]=useState(false);
  const [revision,setRevision]=useState(0);
  useEffect(()=>{setDriverId("");setAck(false);setReason("");},[tripId,skip]);
  useEffect(()=>{
    if(!canAssign)return;
    const c=new AbortController();setLoading(true);setDrivers([]);setDriverId("");
    PdpService.list("",skip,c.signal).then(d=>{if(!c.signal.aborted){setDrivers(d.content);setTotal(d.totalElements);}})
      .catch(e=>{if(!c.signal.aborted)setError(errorMessage(e));})
      .finally(()=>{if(!c.signal.aborted)setLoading(false);});return()=>c.abort();
  },[skip,revision,canAssign]);
  const trip=trips.find(t=>String(t.jobBreakupDetailId)===tripId);
  const selected=drivers.find(d=>String(d.driverId)===driverId);
  const expired=selected?.status==="EXPIRED";
  async function assign(){
    if(!trip || !selected || busy || (expired && (!ack || !reason.trim())))return;
    setBusy(true);setError("");
    try{await PdpService.assign(Number(tripId),Number(driverId),ack,reason.trim());setDriverId("");setAck(false);setReason("");await onAssigned();setRevision(r=>r+1);}
    catch(e){setError(errorMessage(e));setRevision(r=>r+1);}finally{setBusy(false);}
  }
  if(!canAssign || trips.length===0)return null;
  return <section aria-label="Assign driver with PDP check" style={{padding:16}}>
    <h3>Assign driver</h3>
    <label>Trip <select value={tripId} disabled={busy} onChange={e=>setTripId(e.target.value)}>
      <option value="">Select trip</option>{trips.map(t=><option key={t.jobBreakupDetailId} value={t.jobBreakupDetailId}>{t.containerNumber || "Unnumbered container"} - {t.transporterIdName?.name}</option>)}
    </select></label>
    <label> Driver <select value={driverId} disabled={busy || loading || !trip} onChange={e=>{setDriverId(e.target.value);setAck(false);setReason("");}}>
      <option value="">Select driver</option>{drivers.filter(d=>d.transporterId===trip?.transporterIdName?.id).map(d=><option key={d.driverId} value={d.driverId}>{d.driverName} - PDP {d.status.replace(/_/g," ")}</option>)}
    </select></label>
    <button type="button" disabled={skip===0 || busy || loading} onClick={()=>setSkip(s=>Math.max(0,s-30))}>Previous drivers</button>
    <button type="button" disabled={skip+30>=total || busy || loading} onClick={()=>setSkip(s=>s+30)}>More drivers</button>
    {loading && <p role="status">Loading driver PDP details...</p>}
    {selected?.status==="MISSING" && <p role="status">PDP details are missing. Confirm port eligibility before assigning.</p>}
    {selected?.status==="EXPIRING_SOON" && <p role="status">PDP expires soon on {selected.pdpExpiryDate}.</p>}
    {expired && <div role="alert"><p>PDP expired on {selected?.pdpExpiryDate}. The driver may be refused port entry.</p>
      <label><input type="checkbox" checked={ack} disabled={busy} onChange={e=>setAck(e.target.checked)}/> I acknowledge the expired PDP warning.</label>
      <label> Override reason <input maxLength={500} value={reason} disabled={busy} onChange={e=>setReason(e.target.value)}/></label>
      <p>Your identity, reason and PDP expiry will be recorded with this assignment.</p>
    </div>}
    {error && <p role="alert">{error}</p>}
    <button type="button" onClick={assign} disabled={busy || loading || !selected || !trip || (expired && (!ack || !reason.trim()))}>{busy?"Assigning...":"Assign driver"}</button>
  </section>;
}
